import { useTogglePlanStatus } from '../hooks/usePlanMutations';
import { PlanVisibility } from '../types/plans.types';
import { ConfirmDialog } from '@/shared/components/ahkinia/ConfirmDialog/ConfirmDialog';
import { Notification } from '@/shared/components/ahkinia/Notification/notification';

interface TogglePlanModalProps {
  planId: string;
  planName: string;
  currentStatus: boolean;
  visibility: PlanVisibility;
  /** Planes públicos activos sin contar este, para avisar del tope antes de intentarlo. */
  activePublicCount: number;
  open: boolean;
  onClose: () => void;
}

/** El onboarding muestra tres tarjetas; un cuarto plan público rompería esa pantalla. */
const MAX_ACTIVE_PUBLIC_PLANS = 3;

export function TogglePlanModal({
  planId,
  planName,
  currentStatus,
  visibility,
  activePublicCount,
  open,
  onClose,
}: TogglePlanModalProps) {
  const { toggleStatus, isToggling } = useTogglePlanStatus();

  const isEnabling = !currentStatus;
  const wouldExceedPublicCap =
    isEnabling &&
    visibility === PlanVisibility.Public &&
    activePublicCount >= MAX_ACTIVE_PUBLIC_PLANS;

  const handleConfirm = () => {
    toggleStatus(
      { planId, isActive: isEnabling },
      {
        onSuccess: () => {
          Notification.success({
            title: isEnabling ? 'Plan activado exitosamente' : 'Plan retirado exitosamente',
          });
          onClose();
        },
      },
    );
  };

  return (
    <ConfirmDialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title={isEnabling ? 'Activar plan' : 'Retirar plan'}
      description={
        <span data-testid="toggle-plan-confirmation-text">
          {isEnabling
            ? `El plan "${planName}" volverá a ofrecerse.`
            : `El plan "${planName}" dejará de ofrecerse. Las cuentas que ya lo tienen siguen igual: conservan su plan, sus límites y su cobro.`}
        </span>
      }
      confirmLabel={isEnabling ? 'Activar' : 'Retirar'}
      cancelLabel="Cancelar"
      confirmActionType={isEnabling ? 'submit' : 'destructive'}
      onConfirm={handleConfirm}
      isPending={isToggling}
      data-testid="toggle-plan-dialog"
    >
      {wouldExceedPublicCap && (
        <p data-testid="public-plan-cap-warning" className="text-warn text-xs">
          Ya hay {MAX_ACTIVE_PUBLIC_PLANS} planes públicos activos, que es el máximo que cabe en el
          onboarding. Retira uno antes de activar este, o hazlo privado.
        </p>
      )}
    </ConfirmDialog>
  );
}
